import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import type { Location } from 'history';

import { useSidePanelStateActions } from '@confluence/side-panel-state-container';

import { useWarningDialogStateActions } from '../providers/IssueCreateWarningDialogContextProvider';
import {
	AiStates,
	ErrorStates,
	useBulkCreateContext,
} from '../providers/BulkCreateContextProvider';

import { useBulkCreateLoggingEvents } from './useBulkCreateLoggingEvents';

export const useCreateWarningDialogNav = (onClose: () => void) => {
	const { hideSidePanel } = useSidePanelStateActions();
	const { showWarningDialog, hideWarningDialog } = useWarningDialogStateActions();
	const [{ bulkIssuesList, aiState, error, isCreatedWithAi }] = useBulkCreateContext();
	const { logAiInteractionDismissed } = useBulkCreateLoggingEvents();
	const [nextLocation, setNextLocation] = useState<Location>();
	const history = useHistory();

	const isLoading = aiState === AiStates.LOADING;
	const isError = error !== ErrorStates.EMPTY;

	const handleConfirm = useCallback(() => {
		if (isCreatedWithAi && (isLoading || isError || bulkIssuesList.length === 0)) {
			logAiInteractionDismissed();
		}
		hideWarningDialog();
		if (nextLocation) {
			history.block(() => {});
			history.push(nextLocation.pathname);
		}
		hideSidePanel();
		onClose();
	}, [
		bulkIssuesList,
		hideSidePanel,
		hideWarningDialog,
		history,
		isCreatedWithAi,
		isError,
		isLoading,
		logAiInteractionDismissed,
		nextLocation,
		onClose,
	]);

	const handleCancel = useCallback(() => {
		hideWarningDialog();
	}, [hideWarningDialog]);

	useEffect(() => {
		const unblockNavigation = history.block((location) => {
			setNextLocation(location);
			showWarningDialog();
			return false;
		});

		return () => unblockNavigation && unblockNavigation();
	}, [history, showWarningDialog]);

	return {
		handleConfirm,
		handleCancel,
	};
};
